/**
 * CRM enrichment runner (S8) — the per-profile "Enrich this profile" button + status chip.
 *
 * Rendered on an org / person profile card. Brad-only pilot: renders NOTHING unless the current user is in
 * the enrichment pilot (useEnrichmentAccess). One click enqueues the org bundle (a person is enriched
 * within their org bundle, pinned — the backend resolves + labels "Enrich {Person} + {Org}"); the chip
 * reflects the latest queue state (queued → enriched / failed / deferred / skipped) and polls while queued.
 */
import { useEnrichmentAccess, useEnrichmentStatus, useEnrichProfile } from '../../hooks/useCrmEnrichment';
import type { EnrichState } from '../../hooks/useCrmEnrichment';
import styles from './crm.module.css';

const CHIP_LABEL: Record<EnrichState, string> = {
  none: '',
  queued: '⏳ queued',
  enriched: '✦ enriched',   // ✦ = machine-derived provenance glyph (services-spec §6.1)
  failed: '⚠ failed',
  deferred: '⏸ deferred',
  skipped: '— skipped',
};

// A deferral is not always a budget pause — E-9's org-first prerequisite defers too, and labeling that
// healthy state "(budget)" sent the operator hunting for a budget problem that did not exist
// (person:421). The chip now names the recorded reason.
function deferLabel(reason?: string | null): string {
  if (!reason) return '⏸ deferred';
  if (reason === 'awaiting_org_enrichment') return '⏸ enriching employer org first';
  // EnrichmentDeferred is the Anthropic-API-failure defer (provider outage / credit exhaustion), NOT a
  // budget pause — lumping it into "(budget)" re-created the exact D-10 wild-goose chase during the
  // 2026-07-28 credit outage, with every budget at headroom.
  if (/EnrichmentDeferred/i.test(reason)) return '⏸ deferred (API error — retries later)';
  if (/budget|paused|LlmBudgetPaused/i.test(reason)) return '⏸ deferred (budget)';
  return `⏸ deferred (${reason})`;
}

export function CrmEnrichButton({ kind, id }: { kind: 'org' | 'person'; id: number }) {
  const access = useEnrichmentAccess();
  const isPilot = access.data?.pilot === true;
  const status = useEnrichmentStatus(kind, id, isPilot);
  const enrich = useEnrichProfile();

  if (!isPilot) return null;   // brad-only pilot — nothing renders for non-pilot users

  const chip = status.data;
  const busy = enrich.isPending || chip?.state === 'queued';
  const onClick = () =>
    enrich.mutate(kind === 'org' ? { kind, org_id: id } : { kind, person_id: id });

  return (
    <div className={styles.enrichRow}>
      <button className={styles.enrichBtn} onClick={onClick} disabled={busy}
              title="Enrich this profile (mail/calendar signals + public web → structured record)">
        {busy ? 'Enriching…' : (enrich.data?.label || 'Enrich this profile')}
      </button>
      {chip && chip.state !== 'none' && (
        <span className={styles.enrichChip} data-state={chip.state}
              title={chip.last_error || chip.finished_at || undefined}>
          {chip.state === 'deferred' ? deferLabel(chip.defer_reason) : (CHIP_LABEL[chip.state] || chip.state)}
          {chip.state === 'enriched' && chip.finished_at ? ` ${chip.finished_at.slice(0, 10)}` : ''}
        </span>
      )}
      {enrich.data?.status === 'skipped' && (
        <span className={styles.enrichChip} data-state="skipped"
              title={enrich.data.detail || undefined}>— no org context</span>
      )}
    </div>
  );
}
